/**
 * 水印配置工具
 *
 * 生成 Ant Design App 组件 watermark 属性，或独立使用的 canvas 水印。
 *
 */

// ===== 类型定义 =====

/**
 * 水印配置选项
 */
export interface WatermarkOptions {
  /** 水印文字内容 (如用户名, 可以是字符串或数组) */
  content?: string | string[];
  /** 是否启用, 默认 true */
  enabled?: boolean;
  /** 字体大小, 默认 14 */
  fontSize?: number;
  /** 字体颜色, 默认 'rgba(0, 0, 0, 0.15)' */
  fontColor?: string;
  /** 旋转角度 (度), 默认 -22 */
  rotate?: number;
  /** 水印之间的间距 [水平, 垂直], 默认 [100, 100] */
  gap?: [number, number];
  /** 水印相对于容器的偏移 [x, y] */
  offset?: [number, number];
  /** z-index, 默认 9 */
  zIndex?: number;
  /** 字体族, 默认 sans-serif */
  fontFamily?: string;
  /** 字体粗细, 默认 'normal' */
  fontWeight?: 'normal' | 'light' | 'bold' | number;
}

/**
 * Ant Design Watermark props 子集
 */
export interface AntdWatermarkProps {
  content?: string | string[];
  font?: {
    fontSize?: number;
    color?: string;
    fontFamily?: string;
    fontWeight?: 'normal' | 'light' | 'bold' | number;
  };
  rotate?: number;
  gap?: [number, number];
  offset?: [number, number];
  zIndex?: number;
}

// ===== 工厂函数 =====

/**
 * 创建 Ant Design Watermark 组件的 props
 *
 * @param options - 水印配置
 * @returns 当 enabled=false 或 content 为空时返回 undefined, 否则返回 Watermark props
 *
 * @example
 * ```tsx
 * import { Watermark } from 'antd';
 *
 * const watermarkProps = createWatermarkConfig({
 *   content: currentUser?.name,
 *   enabled: process.env.NODE_ENV === 'production',
 * });
 *
 * // 在 layout childrenRender 中使用
 * const layout = {
 *   childrenRender: (children) => watermarkProps
 *     ? <Watermark {...watermarkProps}>{children}</Watermark>
 *     : children,
 * };
 * ```
 *
 * @example
 * ```tsx
 * // 多行水印
 * const watermarkProps = createWatermarkConfig({
 *   content: [currentUser?.name ?? '', dayjs().format('YYYY-MM-DD')],
 *   fontSize: 12,
 *   fontColor: 'rgba(0, 0, 0, 0.1)',
 * });
 * ```
 */
export function createWatermarkConfig(
  options: WatermarkOptions,
): AntdWatermarkProps | undefined {
  const {
    content,
    enabled = true,
    fontSize = 14,
    fontColor = 'rgba(0, 0, 0, 0.15)',
    rotate = -22,
    gap = [100, 100],
    offset,
    zIndex = 9,
    fontFamily = 'sans-serif',
    fontWeight = 'normal',
  } = options;

  // 不启用或无内容时返回 undefined
  if (!enabled) return undefined;

  if (!content) return undefined;

  // 内容为空字符串或空数组
  if (Array.isArray(content) && content.every((c) => !c)) return undefined;
  if (typeof content === 'string' && !content.trim()) return undefined;

  const result: AntdWatermarkProps = {
    content,
    font: {
      fontSize,
      color: fontColor,
      fontFamily,
      fontWeight,
    },
    rotate,
    gap,
    zIndex,
  };

  if (offset) {
    result.offset = offset;
  }

  return result;
}

/**
 * 创建简单的用户水印配置
 *
 * 常见场景的快捷方式: 仅传用户名即可。
 *
 * @param userName - 用户名
 * @param enabled - 是否启用，默认 true。消费方应根据自身环境判断：
 *   - UmiJS: `process.env.NODE_ENV === 'production'`
 *   - Vite: `import.meta.env.PROD`
 * @returns Watermark props 或 undefined
 *
 * @example
 * ```tsx
 * const watermarkProps = createUserWatermark(currentUser?.name, import.meta.env.PROD);
 * ```
 */
export function createUserWatermark(
  userName?: string | null,
  enabled = true,
): AntdWatermarkProps | undefined {
  if (!userName) return undefined;

  return createWatermarkConfig({
    content: userName,
    enabled,
    fontSize: 14,
    fontColor: 'rgba(0, 0, 0, 0.12)',
  });
}
